import React, { useRef, memo } from 'react';
import { SimpleReveal } from './SimpleReveal';
import { X, Check } from 'lucide-react';
import damoPhoto from '@/assets/damian-keyes-founder.png';

const FounderMessageSectionComponent = () => {
  const credentials = ["Co-Founded Europe's Largest Music University, BIMM", "Co-Founded The World's Largest Online Music Business Academy", "BIMM currently valued at $300M with over 9,000+ students a year", "Over 15M+ YouTube views", "Over 230K+ YouTube Subscribers", "Over 30,000 Artists Joined DK Music Business Academy"];
  
  const sectionRef = useRef<HTMLElement>(null);

  return <section ref={sectionRef} id="founder" className="relative z-20 scroll-mt-24 lg:scroll-mt-32 py-12 lg:py-20" style={{ backgroundColor: 'hsl(0, 0%, 3%)' }}>
      {/* Subtle grid continuation - matches hero */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(0, 113, 227, 0.02) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 113, 227, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          backgroundPosition: 'center center',
          maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 95%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 95%)'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Desktop Layout - 2 Column Grid */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-8 items-start">
          {/* Left Column - Photo & Credentials */}
          <div className="space-y-8 sticky top-24 relative z-10">
            <SimpleReveal>
              <div className="block">
                {/* Photo */}
                <div className="relative overflow-visible">
                  {/* Static Blue Glow */}
              <div 
                className="absolute -inset-12 rounded-2xl opacity-50 z-0"
                style={{
                  background: 'radial-gradient(circle at center, rgba(0,113,227,0.28), transparent 80%)',
                  filter: 'blur(24px)'
                }}
              />
                  <img
                    src={damoPhoto} 
                    alt="Damian Keyes, Founder of DK Artists" 
                    className="relative z-10 w-full max-w-md mx-auto rounded-2xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)] transition-all duration-500 hover:shadow-[0_30px_70px_-10px_rgba(0,0,0,0.6)] hover:-translate-y-2 hover:scale-[1.01]"
                    loading="lazy"
                    decoding="async"
                    width="448"
                    height="448"
                  />
                </div>
                
                {/* Name/Title */}
                <div className="text-center max-w-md mx-auto mt-8">
                  <h3 className="text-white text-2xl font-semibold mb-1">Damian Keyes</h3>
                  <p className="text-white/60 text-sm">Founder</p>
                </div>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={80}>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 max-w-md mx-auto">
                <div className="grid grid-cols-1 gap-3">
                  {credentials.map((credential, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0071e3] mt-2 flex-shrink-0" />
                      <p className="text-white/70 text-sm leading-relaxed">
                        {credential}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </SimpleReveal>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-6">
            <SimpleReveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                <span className="font-mono text-xs font-medium text-[#0071e3] tracking-[0.15em] uppercase">
                  &gt; MY STORY
                </span>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={80}>
              <h2 className="headline-lg text-white mb-4">Most Artists Don't Fail For a Lack Of Talent...</h2>
            </SimpleReveal>

            <SimpleReveal delay={160}>
              <div className="space-y-4 text-white/80 body-large">
                <p>They fail because they never learned how to grow.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={240}>
              <div className="space-y-4 text-white/80 body-large">
                <p>I was one of them.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={320}>
              <div className="space-y-4 text-white/80 body-large">
                <p>When I signed my first record deal at 18, I said the same thing every young artist says.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={400}>
              <div className="space-y-4 text-white/80 body-large">
                <p><em>I'll do anything. Just tell me what to do.</em></p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={480}>
              <div className="space-y-4 text-white/80 body-large">
                <p>No one did.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={560}>
              <div className="space-y-4 text-white/80 body-large">
                <p>Six months later, I was dropped.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={640}>
              <div className="space-y-4 text-white/80 body-large">
                <p>I remember the confusion. The noise. Everyone telling me to work harder, post more, write more, hustle more. But no one ever told me how to <em>actually</em> grow.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={720}>
              <div className="space-y-4 text-white/80 body-large">
                <p>I didn't need motivation. I needed direction.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={800}>
              <div className="space-y-4 text-white/80 body-large">
                <p>Most artists are in that same place now. They're not failing because they're lazy or untalented. They're failing because they're trying to do everything alone, without the right structure to turn their effort into progress.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={880}>
              <div className="space-y-3 my-6">
                {["They post more content, hoping something will finally catch on.", "They release songs and see a spike that fades a week later.", "They keep showing up, but the momentum never sticks."].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/80 body-large">{item}</p>
                  </div>
                ))}
              </div>
            </SimpleReveal>

            <SimpleReveal delay={960}>
              <div className="space-y-4 text-white/80 body-large">
                <p>That's why I built DK Artists.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={1040}>
              <div className="space-y-4 text-white/80 body-large">
                <p>To give artists what I never had.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={1120}>
              <div className="space-y-4 text-white/80 body-large">
                <p className="font-semibold text-white">Clarity. Direction. A team that understands how to turn creativity into real, lasting growth.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={1200}>
              <div className="space-y-4 text-white/80 body-large">
                <p>We help you build the foundation around your music, so every release, every post, and every move you make compounds over time.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={1280}>
              <div className="space-y-4 text-white/80 body-large">
                <p>Because real growth doesn't happen by accident.</p>
              </div>
            </SimpleReveal>

            <SimpleReveal delay={1360}>
              <div className="space-y-4 text-white/80 body-large">
                <p className="font-semibold text-white">It happens by design.</p>
              </div>
            </SimpleReveal>
          </div>
        </div>

        {/* iPad Layout - Horizontal: Image left, Accomplishments right */}
        <div className="hidden md:grid lg:hidden md:grid-cols-[1fr_1.2fr] gap-6 mb-8">
          {/* Left - Photo + Name/Title */}
          <div className="flex flex-col items-center">
            <SimpleReveal>
              <div className="inline-flex items-center gap-2 px-2 py-1 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md mb-4">
                <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider uppercase">
                  &gt; MY STORY
                </span>
              </div>
            </SimpleReveal>
            
            <SimpleReveal delay={80}>
              <h2 className="headline-lg text-white mb-4 text-center">Meet Damian Keyes</h2>
            </SimpleReveal>
            
            <SimpleReveal delay={160}>
              <div className="relative overflow-visible">
                {/* Static Blue Glow */}
                <div 
                  className="absolute -inset-12 rounded-xl opacity-60 z-0"
                  style={{
                    background: 'radial-gradient(circle at center, rgba(0,113,227,0.35), transparent 80%)',
                    filter: 'blur(24px)'
                  }}
                />
                <img
                  src={damoPhoto} 
                  alt="Damian Keyes, Founder of DK Artists" 
                  className="relative z-10 w-full max-w-xs rounded-xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)]"
                  loading="lazy"
                  decoding="async"
                  width="448"
                  height="448"
                />
              </div>
              
              <div className="text-center mt-4">
                <h3 className="text-white text-xl font-semibold mb-1">Damian Keyes</h3>
                <p className="text-white/60 text-sm">Founder</p>
              </div>
            </SimpleReveal>
          </div>

          {/* Right - Accomplishments */}
          <div className="flex items-center">
            <SimpleReveal delay={240}>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 h-fit">
                <div className="grid grid-cols-1 gap-3">
                  {credentials.map((credential, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0071e3] mt-2 flex-shrink-0" />
                      <p className="text-white/70 text-sm leading-relaxed">
                        {credential}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </SimpleReveal>
          </div>
        </div>

        {/* iPad - Story content below (full width) */}
        <div className="hidden md:block lg:hidden space-y-6">
          <SimpleReveal delay={320}>
            <h2 className="headline-lg text-white mb-4">Most Artists Don't Fail For a Lack Of Talent...</h2>
          </SimpleReveal>

          <SimpleReveal delay={400}>
            <div className="space-y-4 text-white/80 body-large">
              <p>They fail because they never learned how to grow.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={480}>
            <div className="space-y-4 text-white/80 body-large">
              <p>I was one of them.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={560}>
            <div className="space-y-4 text-white/80 body-large">
              <p>When I signed my first record deal at 18, I said the same thing every young artist says.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={640}>
            <div className="space-y-4 text-white/80 body-large">
              <p><em>I'll do anything. Just tell me what to do.</em></p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={720}>
            <div className="space-y-4 text-white/80 body-large">
              <p>No one did.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={800}>
            <div className="space-y-4 text-white/80 body-large">
              <p>Six months later, I was dropped.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={880}>
            <div className="space-y-4 text-white/80 body-large">
              <p>I remember the confusion. The noise. Everyone telling me to work harder, post more, write more, hustle more. But no one ever told me how to <em>actually</em> grow.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={960}>
            <div className="space-y-4 text-white/80 body-large">
              <p>I didn't need motivation. I needed direction.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1040}>
            <div className="space-y-4 text-white/80 body-large">
              <p>Most artists are in that same place now. They're not failing because they're lazy or untalented. They're failing because they're trying to do everything alone, without the right structure to turn their effort into progress.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1120}>
            <div className="space-y-3 my-6">
              {["They post more content, hoping something will finally catch on.", "They release songs and see a spike that fades a week later.", "They keep showing up, but the momentum never sticks."].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/80 body-large">{item}</p>
                </div>
              ))}
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1200}>
            <div className="space-y-4 text-white/80 body-large">
              <p>That's why I built DK Artists.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1280}>
            <div className="space-y-4 text-white/80 body-large">
              <p>To give artists what I never had.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1360}>
            <div className="space-y-4 text-white/80 body-large">
              <p className="font-semibold text-white">Clarity. Direction. A team that understands how to turn creativity into real, lasting growth.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1440}>
            <div className="space-y-4 text-white/80 body-large">
              <p>We help you build the foundation around your music, so every release, every post, and every move you make compounds over time.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1520}>
            <div className="space-y-4 text-white/80 body-large">
              <p>Because real growth doesn't happen by accident.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1600}>
            <div className="space-y-4 text-white/80 body-large">
              <p className="font-semibold text-white">It happens by design.</p>
            </div>
          </SimpleReveal>
        </div>

        {/* Mobile Layout - Single Column */}
        <div className="block md:hidden space-y-6">
          <SimpleReveal>
            <div className="inline-flex items-center gap-2 px-2 py-1 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md shadow-[0_0_20px_rgba(0,113,227,0.3)]">
              <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider uppercase">
                &gt; MY STORY
              </span>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={80}>
            <h2 className="headline-lg text-white mb-4">Meet Damian Keyes</h2>
          </SimpleReveal>

          {/* Photo + Name Card */}
          <SimpleReveal delay={160}>
            <div className="flex flex-col items-center gap-3">
              <div className="relative overflow-visible">
                {/* Static Blue Glow */}
              <div 
                className="absolute -inset-12 rounded-xl opacity-60 z-0"
                style={{
                  background: 'radial-gradient(circle at center, rgba(0,113,227,0.35), transparent 80%)',
                  filter: 'blur(24px)'
                }}
              />
                <img
                  src={damoPhoto} 
                  alt="Damian Keyes, Founder of DK Artists" 
                  className="relative z-10 w-48 h-auto rounded-xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)]"
                  loading="lazy"
                  decoding="async"
                  width="448"
                  height="448"
                />
              </div>
              
              <div className="text-center">
                <h3 className="text-white text-lg font-semibold mb-0.5">Damian Keyes</h3>
                <p className="text-white/60 text-xs">Founder</p>
              </div>
            </div>
          </SimpleReveal>

          {/* Credentials */}
          <SimpleReveal delay={240}>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="grid grid-cols-1 gap-2">
                {credentials.map((credential, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0071e3] mt-1.5 flex-shrink-0" />
                    <p className="text-white/70 text-xs leading-relaxed">
                      {credential}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={320}>
            <h2 className="headline-lg text-white mb-4">Most Artists Don't Fail For a Lack Of Talent...</h2>
          </SimpleReveal>

          {/* Story Content */}
          <SimpleReveal delay={400}>
            <div className="space-y-4 text-white/80 body-large">
              <p>They fail because they never learned how to grow.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={480}>
            <div className="space-y-4 text-white/80 body-large">
              <p>I was one of them.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={560}>
            <div className="space-y-4 text-white/80 body-large">
              <p>When I signed my first record deal at 18, I said the same thing every young artist says.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={640}>
            <div className="space-y-4 text-white/80 body-large">
              <p><em>I'll do anything. Just tell me what to do.</em></p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={720}>
            <div className="space-y-4 text-white/80 body-large">
              <p>No one did.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={800}>
            <div className="space-y-4 text-white/80 body-large">
              <p>Six months later, I was dropped.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={880}>
            <div className="space-y-4 text-white/80 body-large">
              <p>I remember the confusion. The noise. Everyone telling me to work harder, post more, write more, hustle more. But no one ever told me how to <em>actually</em> grow.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={960}>
            <div className="space-y-4 text-white/80 body-large">
              <p>I didn't need motivation. I needed direction.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1040}>
            <div className="space-y-4 text-white/80 body-large">
              <p>Most artists are in that same place now. They're not failing because they're lazy or untalented. They're failing because they're trying to do everything alone, without the right structure to turn their effort into progress.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1120}>
            <div className="space-y-3 my-6">
              {["They post more content, hoping something will finally catch on.", "They release songs and see a spike that fades a week later.", "They keep showing up, but the momentum never sticks."].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/80 body-large">{item}</p>
                </div>
              ))}
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1200}>
            <div className="space-y-4 text-white/80 body-large">
              <p>That's why I built DK Artists.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1280}>
            <div className="space-y-4 text-white/80 body-large">
              <p>To give artists what I never had.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1360}>
            <div className="space-y-4 text-white/80 body-large">
              <p className="font-semibold text-white">Clarity. Direction. A team that understands how to turn creativity into real, lasting growth.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1440}>
            <div className="space-y-4 text-white/80 body-large">
              <p>We help you build the foundation around your music, so every release, every post, and every move you make compounds over time.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1520}>
            <div className="space-y-4 text-white/80 body-large">
              <p>Because real growth doesn't happen by accident.</p>
            </div>
          </SimpleReveal>

          <SimpleReveal delay={1600}>
            <div className="space-y-4 text-white/80 body-large">
              <p className="font-semibold text-white">It happens by design.</p>
            </div>
          </SimpleReveal>
        </div>
      </div>
    </section>;
};

export const FounderMessageSection = memo(FounderMessageSectionComponent);
export default FounderMessageSection;